/** @typedef {import('swiper').Swiper} Swiper */
import {Autoplay} from 'swiper/modules';

import {
    BP_MD,
    BP_SM,
    MOBILE_SLIDES_PER_VIEW,
    PEEK_GRID_SLIDES_PER_VIEW,
    TABLET_SLIDES_PER_VIEW,
} from './carousel.breakpoints.js';
import {
    bindAutoplayClassToggle,
    DEFAULT_SLIDE_GAP_PX,
    getLoopAdditionalSlides,
    getSlidesPerView,
    prefersReducedMotion,
    readCssLengthPx,
} from './carousel.util.js';

export {
    BP_MD,
    BP_SM,
    MOBILE_SLIDES_PER_VIEW,
    PEEK_GRID_SLIDES_PER_VIEW,
    TABLET_SLIDES_PER_VIEW,
} from './carousel.breakpoints.js';

/**
 * @typedef {object} CarouselBuildContext
 * @property {HTMLElement} $host
 * @property {HTMLElement} $swiperEl
 * @property {object} settings
 * @property {string} navMode
 * @property {boolean} syncActiveSlides
 */

/**
 * @typedef {object} CarouselBuildResult
 * @property {HTMLElement|string} swiperEl
 * @property {object} swiperOptions
 * @property {import('swiper/types').SwiperModule[]} [modules]
 * @property {(swiper: Swiper | null) => void} [afterInit]
 * @property {boolean} [skipSwiper]
 */

// -----------------------------------------------------------------------------
// Peek-grid layout (four logical slides, outer two ~30% clipped)
// -----------------------------------------------------------------------------
export const PEEK_GRID_LOGICAL_SLIDES = 4;
export const PEEK_GRID_FOCAL_INDEX = 2;

export const SWIPER_SPEED_MS = 400;
export const AUTOPLAY_DELAY_MS = 5000;
export const CENTERED_MAX_SLIDES_PER_VIEW = 3;

export const CSS_VAR_SLIDE_GAP = '--carousel-slide-gap';
export const CSS_VAR_SYNC_WIDTH = '--carousel-sync-width';
export const CSS_VAR_SYNC_INSET = '--carousel-sync-inset-start';

export const CSS_SLIDE_GAP_FALLBACK =
    `var(${CSS_VAR_SLIDE_GAP}, var(--space-gutter, 20px))`;

const MD_MEDIA_QUERY = `(min-width: ${BP_MD}px)`;

/**
 * @typedef {'peek-grid' | 'centered'} ExternalSyncLayout
 */

/**
 * @typedef {object} ExternalSyncPresetConfig
 * @property {ExternalSyncLayout} layout
 * @property {string} swiperSelector
 * @property {string} textBlockSelector
 * @property {string} nextButtonSelector
 * @property {string} [sectionSelector] - root for autoplay class toggle
 * @property {string} [alignAnchorSelector] - peek-grid alignment target
 * @property {string} [activeTextClass='is-active']
 * @property {string} slideGapVar
 * @property {string} metaWidthVar
 * @property {string} metaInsetVar
 * @property {string} [autoplayDisabledClass]
 */

/**
 * Media track + external caption row (module supplies selectors and layout).
 * @param {CarouselBuildContext} ctx
 * @param {ExternalSyncPresetConfig} config
 * @returns {CarouselBuildResult}
 */
export function buildExternalSyncCarousel(ctx, config) {
    const {$host} = ctx;
    const {
        layout,
        swiperSelector,
        textBlockSelector,
        nextButtonSelector,
        sectionSelector,
        alignAnchorSelector,
        activeTextClass = 'is-active',
        slideGapVar,
        metaWidthVar,
        metaInsetVar,
        autoplayDisabledClass,
    } = config;

    const isPeekGrid = layout === 'peek-grid';

    const $swiperEl = /** @type {HTMLElement} */ (
        $host.querySelector(swiperSelector)
    );
    const $textBlocks = $host.querySelectorAll(textBlockSelector);
    const $btnNext = $host.querySelector(nextButtonSelector);
    const $section = sectionSelector ?
        $host.closest(sectionSelector) :
        null;

    const slideCount =
        parseInt($host.dataset.slideCount, 10) || $textBlocks.length;
    const autoplayEnabled =
        $host.dataset.autoplay === 'true' && !prefersReducedMotion();

    if (slideCount <= 1) {
        $textBlocks.forEach(($el, i) => {
            $el.classList.toggle(activeTextClass, i === 0);
        });
        if ($btnNext) {
            /** @type {HTMLElement} */ ($btnNext).hidden = true;
        }
        return {
            skipSwiper: true,
            afterInit: () => {},
        };
    }

    const desktopPerView = isPeekGrid ?
        PEEK_GRID_SLIDES_PER_VIEW :
        Math.min(CENTERED_MAX_SLIDES_PER_VIEW, slideCount);

    const domSlideCount =
        $swiperEl.querySelectorAll('.swiper-slide').length;
    const loopAdditionalSlides = getLoopAdditionalSlides(
        domSlideCount,
        desktopPerView,
    );

    const isPeekGridDesktop = (swiper) => {
        if (!isPeekGrid) return false;
        if (getSlidesPerView(swiper) > 2) return true;
        return window.matchMedia(MD_MEDIA_QUERY).matches;
    };

    const getLogicalIndex = (swiper) => swiper.realIndex % slideCount;

    const getFocalOffset = (swiper) => {
        if (isPeekGridDesktop(swiper)) {
            return PEEK_GRID_FOCAL_INDEX;
        }
        const perView = getSlidesPerView(swiper);
        if (perView >= CENTERED_MAX_SLIDES_PER_VIEW) {
            return Math.floor((perView - 1) / 2);
        }
        return 0;
    };

    /**
     * Loop snap index that places a testimonial at the focal visible slot.
     * @param {Swiper} swiper
     * @param {number} testimonialIndex
     * @returns {number}
     */
    const getLoopIndexForFocal = (swiper, testimonialIndex) => {
        const focalOffset = getFocalOffset(swiper);
        if (focalOffset === 0) return testimonialIndex;
        return (
            (testimonialIndex - focalOffset + slideCount) % slideCount
        );
    };

    /**
     * Resolve a slide element to its logical content index in the text track.
     * @param {Element | null | undefined} slideEl
     * @returns {number | null}
     */
    const getSlideContentIndex = (slideEl) => {
        const raw =
            slideEl?.getAttribute('data-testimonial-index') ??
            slideEl?.getAttribute('data-swiper-slide-index');
        if (raw == null) return null;
        const idx = parseInt(raw, 10);
        if (!Number.isFinite(idx)) return null;
        return ((idx % slideCount) + slideCount) % slideCount;
    };

    /**
     * Visible slides in viewport order (left to right).
     * @param {Swiper} swiper
     * @returns {Element[]}
     */
    const getVisibleSlidesSorted = (swiper) => {
        const swiperRect = swiper.el.getBoundingClientRect();
        const visible = [];

        for (const slide of swiper.slides) {
            if (!slide.classList.contains('swiper-slide')) continue;
            const rect = slide.getBoundingClientRect();
            if (rect.width < 1) continue;
            if (rect.right <= swiperRect.left + 1) continue;
            if (rect.left >= swiperRect.right - 1) continue;
            visible.push({slide, left: rect.left});
        }

        visible.sort((a, b) => a.left - b.left);
        return visible.map((entry) => entry.slide);
    };

    /**
     * Peek-grid focal portrait: third visible slot (PEEK_GRID_FOCAL_INDEX).
     * Anchor-left matching hits slot 0; focal hero is two slides ahead.
     * @param {Swiper} swiper
     * @returns {number | null}
     */
    const getPeekGridFocalSlideIndex = (swiper) => {
        const visible = getVisibleSlidesSorted(swiper);
        const focalSlide = visible[PEEK_GRID_FOCAL_INDEX];
        return getSlideContentIndex(focalSlide);
    };

    const getActiveContentIndex = (swiper) => {
        if (isPeekGridDesktop(swiper)) {
            const focalIdx = getPeekGridFocalSlideIndex(swiper);
            if (focalIdx != null) return focalIdx;
        }

        const focalOffset = getFocalOffset(swiper);
        if (focalOffset > 0) {
            const visible = getVisibleSlidesSorted(swiper);
            const focalSlide = visible[focalOffset];
            const idx = getSlideContentIndex(focalSlide);
            if (idx != null) return idx;
        }

        const idx = getSlideContentIndex(
            swiper.el.querySelector('.swiper-slide-active'),
        );
        if (idx != null) return idx;

        return (getLogicalIndex(swiper) + focalOffset) % slideCount;
    };

    const readSlideGapPx = () =>
        readCssLengthPx($host, slideGapVar, DEFAULT_SLIDE_GAP_PX);

    const getSlideMetrics = (swiper, space = readSlideGapPx()) => {
        const perView = getSlidesPerView(swiper);
        const slideWidth =
            (swiper.width - space * (perView - 1)) / perView;
        return {space, slideWidth};
    };

    const computeGridOffsetBefore = (containerWidth, swiperLeft, space) => {
        if (!isPeekGrid) return 0;
        if (!window.matchMedia(MD_MEDIA_QUERY).matches) return 0;

        const $anchor = alignAnchorSelector ?
            $section?.querySelector(alignAnchorSelector) :
            null;
        if (!$anchor || !containerWidth) return 0;
        const slideWidth =
            (containerWidth - space * (PEEK_GRID_SLIDES_PER_VIEW - 1)) /
            PEEK_GRID_SLIDES_PER_VIEW;
        const anchorLeft =
            $anchor.getBoundingClientRect().left - swiperLeft;

        return anchorLeft - PEEK_GRID_FOCAL_INDEX * (slideWidth + space);
    };

    const alignPeekGridFocalSlide = (swiper, gap) => {
        if (!isPeekGridDesktop(swiper)) {
            swiper.params.slidesOffsetBefore = 0;
            return;
        }

        const swiperRect = swiper.el.getBoundingClientRect();
        swiper.params.slidesOffsetBefore = computeGridOffsetBefore(
            swiper.width,
            swiperRect.left,
            gap,
        );
    };

    const syncMetaToVisibleTrack = (swiper, gap = readSlideGapPx()) => {
        if (!isPeekGridDesktop(swiper)) {
            $host.style.removeProperty(metaWidthVar);
            $host.style.removeProperty(metaInsetVar);
            return;
        }
        const {space, slideWidth} = getSlideMetrics(swiper, gap);
        const perView = getSlidesPerView(swiper);
        const containerWidth = $host.clientWidth || swiper.width;
        const visibleTrackWidth =
            perView * slideWidth + (perView - 1) * space; // ≈ swiper.width
        const insetStart = Math.max(
            0,
            Math.round(swiper.params.slidesOffsetBefore || 0),
        );
        const trackWidth = Math.min(
            Math.round(visibleTrackWidth),
            Math.max(0, containerWidth - insetStart),
        );
        $host.style.setProperty(metaWidthVar, `${trackWidth}px`);
        $host.style.setProperty(metaInsetVar, `${insetStart}px`);
    };

    const setActiveText = (swiper) => {
        const activeIndex = getActiveContentIndex(swiper);
        $textBlocks.forEach(($el, i) => {
            $el.classList.toggle(activeTextClass, i === activeIndex);
        });
    };

    const applyCarouselLayout = (swiper, {recenter = false} = {}) => {
        const gap = readSlideGapPx();
        swiper.params.spaceBetween = gap;

        alignPeekGridFocalSlide(swiper, gap);
        swiper.update();

        if (swiper.params.loop && typeof swiper.loopFix === 'function') {
            swiper.loopFix();
        }

        if (recenter) {
            swiper.slideToLoop(getLoopIndexForFocal(swiper, 0), 0);
        }

        syncMetaToVisibleTrack(swiper, gap);
    };

    const onLayoutChange = (swiperInstance) => {
        applyCarouselLayout(swiperInstance);
        setActiveText(swiperInstance);
    };

    const modules = autoplayEnabled ? [Autoplay] : [];
    const gap = readSlideGapPx();
    const swiperRect = $swiperEl.getBoundingClientRect();
    const initialGridOffset = computeGridOffsetBefore(
        $swiperEl.clientWidth,
        swiperRect.left,
        gap,
    );

    const swiperOptions = {
        modules,
        loop: true,
        loopAdditionalSlides,
        loopPreventsSliding: false,
        watchOverflow: false,
        observer: true,
        observeParents: true,
        slidesPerGroup: 1,
        slidesPerView: MOBILE_SLIDES_PER_VIEW,
        spaceBetween: gap,
        slidesOffsetBefore: initialGridOffset,
        speed: SWIPER_SPEED_MS,
        breakpoints: {
            [BP_SM]: {
                slidesPerView: TABLET_SLIDES_PER_VIEW,
                slidesPerGroup: 1,
                spaceBetween: gap,
            },
            [BP_MD]: {
                slidesPerView: desktopPerView,
                spaceBetween: gap,
            },
        },
        autoplay: autoplayEnabled ?
            {
                delay: AUTOPLAY_DELAY_MS,
                disableOnInteraction: false,
            } :
            false,
        on: {
            init(swiperInstance) {
                const applyInitialLayout = () => {
                    applyCarouselLayout(swiperInstance, {recenter: true});
                    setActiveText(swiperInstance);
                };
                requestAnimationFrame(() => {
                    requestAnimationFrame(applyInitialLayout);
                });
            },
            realIndexChange: setActiveText,
            slideChangeTransitionEnd: setActiveText,
            resize: onLayoutChange,
            breakpoint: onLayoutChange,
        },
    };

    return {
        swiperEl: $swiperEl,
        swiperOptions,
        modules,
        afterInit: (swiper) => {
            $btnNext?.addEventListener('click', () => {
                if (swiper.animating || swiper.destroyed) return;
                swiper.slideNext();
            });

            if (autoplayEnabled && swiper.autoplay && $section && autoplayDisabledClass) {
                bindAutoplayClassToggle(
                    $section,
                    swiper,
                    autoplayDisabledClass,
                );
            }
        },
    };
}
